import React, { createContext, useContext, useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useUser } from "@/hooks/useUser";
import { useDocument } from "@/hooks/useDocument";
import { ICompasAIChatAction } from "@/types/compass.types";

interface CompassSocketContextType {
    taskStatuses: Record<string, string>;
    activeProcessingTask: ICompasAIChatAction | null;
    isConnected: boolean;
}

const CompassSocketContext = createContext<CompassSocketContextType | undefined>(undefined);

export const CompassSocketProvider: React.FC<{
    children: React.ReactNode;
    onUpdate?: () => void;
    onMessage?: () => void;
}> = ({ children, onUpdate, onMessage }) => {
    const [taskStatuses, setTaskStatuses] = useState<Record<string, string>>({});
    const [activeProcessingTask, setActiveProcessingTask] = useState<ICompasAIChatAction | null>(null);
    const taskStatusesRef = React.useRef<Record<string, string>>({});
    const [isConnected, setIsConnected] = useState(false);
    const queryClient = useQueryClient();
    const { user } = useUser();
    const { data: doc } = useDocument();

    // Use refs for callbacks to avoid stale closures in socket listener
    const callbacksRef = React.useRef({ onUpdate, onMessage });

    // Ref for debouncing invalidation
    const invalidationTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        callbacksRef.current = { onUpdate, onMessage };
    }, [onUpdate, onMessage]);

    useEffect(() => {
        if (!user || !doc?._id) {
            return;
        }

        const apiUrl = process.env.API_BASE_URL || "http://localhost:8000";
        const url = new URL(apiUrl);
        const wsProtocol = url.protocol === "https:" ? "wss:" : "ws:";
        const socketUrl = `${wsProtocol}//${url.host}/compas-ai/ws/queue-status?user_id=${user.id}&context_id=${doc._id}`;

        const socket = new WebSocket(socketUrl);

        socket.onopen = () => {
            console.log("Compass queue socket connected");
            setIsConnected(true);
        };

        socket.onmessage = (event) => {
            try {
                const message: { type: string; tasks: ICompasAIChatAction[] } = JSON.parse(event.data);
                if (message.type === "update" && message.tasks.length > 0) {
                    const currentStatuses = { ...taskStatusesRef.current };
                    const activeStatuses = ["queued", "processing", "pending"];
                    const completionStatuses = ["processed", "completed", "failed"];

                    // Check if we were processing before this update
                    const wasProcessing = Object.values(currentStatuses).some((status) =>
                        activeStatuses.includes(status)
                    );

                    let hasCompletion = false;
                    let completedCount = 0;
                    const processingTasks: ICompasAIChatAction[] = [];

                    message.tasks.forEach((task) => {
                        const key = task.queue_item_id || task.id;
                        if (key && task.status) {
                            // Check for completion transition
                            if (currentStatuses[key] === "processing" && task.status === "completed") {
                                completedCount++;
                            }

                            currentStatuses[key] = task.status;
                            if (completionStatuses.includes(task.status)) {
                                hasCompletion = true;
                            }
                            if (task.status === "processing") {
                                processingTasks.push(task);
                            }
                        }
                    });

                    if (completedCount > 0) {
                        toast.success(completedCount > 1 ? `${completedCount} actions completed` : "Action completed", { duration: 2000 });
                    }

                    // Update refs/state
                    taskStatusesRef.current = currentStatuses;
                    setTaskStatuses(currentStatuses);

                    const hasActiveTasks = Object.values(currentStatuses).some((status) =>
                        activeStatuses.includes(status)
                    );

                    // 1. Invalidate if ANY task completed (to show results)
                    // DEBOUNCED: Wait 500ms before refreshing to bunch up rapid updates
                    if (hasCompletion) {
                        if (invalidationTimeoutRef.current) {
                            clearTimeout(invalidationTimeoutRef.current);
                        }

                        invalidationTimeoutRef.current = setTimeout(() => {
                            queryClient.invalidateQueries({ queryKey: ["chatHistory", doc._id] });
                            queryClient.invalidateQueries({ queryKey: ["compass-chat-history", doc._id] });

                            // Check if consistent state is now clean (no active tasks) to close dialog
                            const currentRefStatuses = taskStatusesRef.current;
                            const currentHasActive = Object.values(currentRefStatuses).some(s => activeStatuses.includes(s));

                            // Only close if we are clean NOW, AND we were processing when this update batch started.
                            if (!currentHasActive && wasProcessing) {
                                callbacksRef.current.onUpdate?.();
                            }
                            // 3. Ensure we clear the active processing task marker ONLY if no tasks are effectively active
                            if (!currentHasActive) {
                                setActiveProcessingTask(null);
                            }
                        }, 500);
                    }

                    // 2. Manage active processing task state
                    if (processingTasks.length > 0) {
                        // Optimistically set the FIRST processing task as the active one
                        const nextProcessingTask = processingTasks[0];
                        setActiveProcessingTask(nextProcessingTask ?? null);
                        callbacksRef.current.onMessage?.();
                    } else if (hasCompletion) {
                        // If we have completion but no processing tasks, do NOT clear active task immediately.
                        // We wait for the history invalidate to catch up.
                        // The UI will de-dupe activeProcessingTask against the new history.
                        callbacksRef.current.onMessage?.();
                    } else if (!hasActiveTasks) {
                        setActiveProcessingTask(null);
                    }
                }
            } catch (error) {
                console.error("Error parsing socket message:", error);
            }
        };

        socket.onclose = () => {
            console.log("Compass queue socket disconnected");
            setIsConnected(false);
        };

        return () => {
            if (invalidationTimeoutRef.current) {
                clearTimeout(invalidationTimeoutRef.current);
            }
            socket.close();
        };
    }, [user, doc?._id, queryClient]); // Removed onUpdate/onMessage from deps

    return (
        <CompassSocketContext.Provider value={{ taskStatuses, activeProcessingTask, isConnected }}>
            {children}
        </CompassSocketContext.Provider>
    );
};

export const useCompassSocketContext = () => {
    const context = useContext(CompassSocketContext);
    if (context === undefined) {
        throw new Error("useCompassSocketContext must be used within a CompassSocketProvider");
    }
    return context;
};
